/**
 * InstallPromptProvider — app-wide "add to home screen" orchestration.
 *
 * The `beforeinstallprompt` event fires exactly ONCE per page load, and only a
 * listener already mounted at that moment can capture it. So it is captured
 * here at app root and shared through context, letting BOTH the automatic
 * bottom-sheet nudge and the manual "Install app" row on More drive the same
 * native install dialog.
 *
 * Chromium (Android + desktop) fires the event, so those get one-tap install.
 * iOS Safari supports neither `beforeinstallprompt` nor a programmatic install,
 * so callers show manual Add-to-Home-Screen steps instead — which is also why
 * this detects the browser, not just the OS: an agent who opened the link from
 * WhatsApp is sitting in an in-app webview that CANNOT install at all, and needs
 * to be told to open the page in a real browser first.
 *
 * Dismissals persist in localStorage with a 30-day TTL.
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const InstallPromptContext = createContext(null);

const DISMISSED_KEY = 'cid-agent-install-dismissed';
const RESHOW_DAYS = 30;

function detectPlatform(ua) {
  // iOS: iPhone/iPod/iPad, plus iPadOS reporting a desktop UA (Mac + touch).
  const isIOS =
    /iPhone|iPod|iPad/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

/**
 * Which browser the agent is actually in — the install route differs per browser,
 * and in-app webviews have no install route at all.
 *
 * `inApp` is best-effort: the named social apps are matched by UA token, and any
 * other iOS browser with no "Safari/" and no known third-party token is a
 * WKWebView (i.e. some app's built-in browser). Android webviews mark themselves
 * with "; wv".
 */
function detectBrowser(ua, platform) {
  const named = [
    [/FBAN|FBAV|FB_IAB/, 'Facebook'],
    [/Instagram/, 'Instagram'],
    [/MicroMessenger/, 'WeChat'],
    [/\bLine\//, 'LINE'],
    [/WhatsApp/, 'WhatsApp'],
    [/LinkedInApp/, 'LinkedIn'],
    [/TikTok|BytedanceWebview/, 'TikTok'],
  ];
  for (const [re, name] of named) {
    if (re.test(ua)) return { name, inApp: true, isSafari: false };
  }

  if (platform === 'ios') {
    if (/CriOS/.test(ua)) return { name: 'Chrome', inApp: false, isSafari: false };
    if (/FxiOS/.test(ua)) return { name: 'Firefox', inApp: false, isSafari: false };
    if (/EdgiOS/.test(ua)) return { name: 'Edge', inApp: false, isSafari: false };
    if (/OPiOS|OPT\//.test(ua)) return { name: 'Opera', inApp: false, isSafari: false };
    // Real Safari always carries "Safari/". Its absence means an embedded WKWebView.
    if (!/Safari\//.test(ua)) return { name: null, inApp: true, isSafari: false };
    return { name: 'Safari', inApp: false, isSafari: true };
  }

  if (platform === 'android') {
    if (/;\s*wv\)/.test(ua)) return { name: null, inApp: true, isSafari: false };
    if (/SamsungBrowser/.test(ua)) return { name: 'Samsung Internet', inApp: false, isSafari: false };
    if (/EdgA/.test(ua)) return { name: 'Edge', inApp: false, isSafari: false };
    if (/OPR|Opera/.test(ua)) return { name: 'Opera', inApp: false, isSafari: false };
    if (/Firefox/.test(ua)) return { name: 'Firefox', inApp: false, isSafari: false };
    return { name: 'Chrome', inApp: false, isSafari: false };
  }

  return { name: null, inApp: false, isSafari: /^((?!chrome|android).)*safari/i.test(ua) };
}

function isStandalone() {
  // iOS Safari uses the non-standard `navigator.standalone`.
  if ('standalone' in navigator && navigator.standalone) return true;
  // Standard display-mode (Android/Chrome + installed desktop PWAs).
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  return false;
}

function isDismissedRecently() {
  try {
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (!dismissed) return false;
    const days = (Date.now() - new Date(dismissed).getTime()) / (1000 * 60 * 60 * 24);
    return days < RESHOW_DAYS;
  } catch {
    return false;
  }
}

export function InstallPromptProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState('desktop');
  const [browser, setBrowser] = useState({ name: null, inApp: false, isSafari: false });
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // Set when the agent explicitly asks for the sheet (the More row), so it can be
  // reopened on demand even after the 30-day dismissal and with no auto delay.
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || '';
    const p = detectPlatform(ua);
    setPlatform(p);
    setBrowser(detectBrowser(ua, p));
    setInstalled(isStandalone());
    setDismissed(isDismissedRecently());

    const handleBIP = (e) => {
      // Stop Chrome from auto-showing its own mini-infobar; we drive the dialog.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const handleInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBIP);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBIP);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    // Single-use: once prompt() is called the event can't be reused, so clear it
    // whatever the outcome.
    setDeferredPrompt(null);
    return choice;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
    } catch {
      // private mode — the nudge just returns next launch
    }
    setDismissed(true);
    setManualOpen(false);
  }, []);

  const triggerPrompt = useCallback(() => setManualOpen(true), []);
  const closePrompt = useCallback(() => setManualOpen(false), []);

  // The automatic nudge is for phones only — a desktop agent gets the More row
  // but never an unprompted sheet. It needs the native event on Android (no
  // event = no install route worth interrupting for) but shows on iOS regardless,
  // since manual steps are the ONLY route there.
  const autoShow =
    !installed &&
    !dismissed &&
    ((platform === 'android' && deferredPrompt !== null) || platform === 'ios');

  const showPrompt = !installed && (autoShow || manualOpen);

  const value = {
    platform,
    browser,
    installed,
    showPrompt,
    // True only for the automatic nudge, so the sheet can delay that one and open
    // instantly when a button asked for it.
    autoShow,
    manualOpen,
    canInstallNatively: deferredPrompt !== null,
    promptInstall,
    dismiss,
    triggerPrompt,
    closePrompt,
  };

  return <InstallPromptContext.Provider value={value}>{children}</InstallPromptContext.Provider>;
}

export function useInstallPrompt() {
  const ctx = useContext(InstallPromptContext);
  if (!ctx) throw new Error('useInstallPrompt must be used within an InstallPromptProvider');
  return ctx;
}

export default InstallPromptContext;
